<template>
  <div>
    <div class="card border-0 shadow-none ml-1">
      <div class="sidebar-body">
        <div class="form-group mb-4">
          <label class="font-weight-bold">Location</label>
          <multiselect
            ref="locationMultiselect"
            v-model="filters.locationNames"
            :options="locationOptions"
            :loading="locationLoading"
            placeholder="Type to search locations..."
            label="name"
            track-by="@id"
            :multiple="true"
            :taggable="false"
            :hideSelected="false"
            :show-labels="false"
            :close-on-select="false"
            :internal-search="false"
            open-direction="bottom"
            :preserve-search="true"
            @search-change="onLocationSearch"
            @select="onLocationSelect">
            <span slot="noResult">No locations found.</span>
            <template slot="tag" slot-scope="{ option, remove }">
              <span
                class="multiselect__tag datasheet-tag"
                :title="option.name">
                {{ option.name }}
                <i class="multiselect__tag-icon" @click.stop="remove(option)"></i>
              </span>
            </template>
          </multiselect>
        </div>

        <div class="form-group mb-4">
          <label class="font-weight-bold">Datasheet</label>
          <multiselect
            ref="datasheetMultiselect"
            v-model="filters.datasheetNames"
            :options="datasheetOptions"
            placeholder="Type to search datasheets..."
            label="displayName"
            track-by="name"
            :multiple="true"
            :taggable="false"
            :hideSelected="false"
            :show-labels="false"
            :close-on-select="false"
            :internal-search="false"
            open-direction="bottom"
            :preserve-search="true"
            @search-change="onDatasheetSearch"
            @select="onDatasheetSelect">
            <span slot="noResult">No datasheets found.</span>
            <template slot="tag" slot-scope="{ option, remove }">
              <span
                class="multiselect__tag datasheet-tag"
                :title="option.displayName">
                {{ option.displayName }}
                <i class="multiselect__tag-icon" @click.stop="remove(option)"></i>
              </span>
            </template>
          </multiselect>
        </div>

        <div class="form-group mb-3">
          <label class="font-weight-bold mb-1">Observation Year</label>
          <input type="number" class="form-control" v-model="filters.year" placeholder="YYYY">
        </div>

        <div class="form-group mb-3">
          <label class="font-weight-bold">Observed By</label>
          <multiselect
            ref="memberMultiselect"
            v-model="filters.memberNames"
            :options="memberOptions"
            :loading="memberLoading"
            placeholder="Type to search by observer name..."
            label="displayName"
            track-by="@id"
            :multiple="true"
            :taggable="false"
            :hideSelected="false"
            :show-labels="false"
            :close-on-select="false"
            :internal-search="false"
            open-direction="bottom"
            :preserve-search="true"
            @search-change="onMemberSearch"
            @select="onMemberSelect">
            <span slot="noResult">No members found.</span>
            <template slot="tag" slot-scope="{ option, remove }">
              <span
                class="multiselect__tag datasheet-tag"
                :title="option.displayName">
                {{ option.displayName }}
                <i class="multiselect__tag-icon" @click.stop="remove(option)"></i>
              </span>
            </template>
          </multiselect>
        </div>

        <b-form-group>
          <b-form-checkbox size="md" v-model="filters.hasPhotos">
            <span class="font-weight-bold">Show observations with photos</span>
          </b-form-checkbox>
        </b-form-group>

        <hr>

        <div class="row justify-content-between mb-2">
          <button class="btn btn-light border ml-3" @click="resetFilters">
            Clear All
          </button>
          <button class="btn btn-secondary mr-3" @click="applyFilters">
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";

export default {
  name: "FilterSidebar",
  props: {
    initialLocations: {
      type: Array,
      default: () => []
    },
    initialMembers: {
      type: Array,
      default: () => []
    },
    initialFilters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      locationOptions: [],
      memberOptions: [],
      datasheetOptions: [],
      locationLoading: false,
      memberLoading: false,
      locationDebounceTimer: null,
      memberDebounceTimer: null,
      datasheetDebounceTimer: null,
      locationSearchQuery: '',
      datasheetSearchQuery: '',
      memberSearchQuery: '',
      filters: {
        locationNames: [],
        datasheetNames: [],
        year: '',
        memberNames: [],
        hasPhotos: false
      },
    };
  },
  mounted() {
    // Attach listener with capture: true to intercept the event early
    window.addEventListener('keydown', this.enterKeyHandler, true);
  },

  beforeDestroy() {
    // Always clean up your global listeners!
    window.removeEventListener('keydown', this.enterKeyHandler, true);
  },
  watch: {
    initialFilters: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.filters = {
          ...this.filters,
          ...newVal
        };
      }
    },
    initialLocations: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.locationOptions = newVal;
        }
      }
    },
    initialMembers: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.memberOptions = newVal;
        }
      }
    },
    allDatasheets: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.datasheetOptions = newVal.slice(0, 5);
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      allDatasheets: 'project/datasheets/sortedDatasheets',
    }),
  },
  methods: {
    ...mapActions({
      fetchFilterLocations: 'project/get/fetchFilterLocations',
      fetchFilterMembers: 'project/get/fetchFilterMembers',
    }),
    onLocationSearch(query) {
      this.locationSearchQuery = query;
      if (this.locationDebounceTimer) {
        clearTimeout(this.locationDebounceTimer);
      }
      this.locationDebounceTimer = setTimeout(() => {
        this.searchLocations(query);
      }, 1000);
    },
    onMemberSearch(query) {
      this.memberSearchQuery = query;
      if (this.memberDebounceTimer) {
        clearTimeout(this.memberDebounceTimer);
      }
      this.memberDebounceTimer = setTimeout(() => {
        this.searchMembers(query);
      }, 1000);
    },
    onDatasheetSearch(query) {
      this.datasheetSearchQuery = query;
      if (this.datasheetDebounceTimer) {
        clearTimeout(this.datasheetDebounceTimer);
      }
      this.datasheetDebounceTimer = setTimeout(() => {
        this.filterDatasheets(query);
      }, 1000);
    },
    onLocationSelect() {
      this.$nextTick(() => {
        if (this.$refs.locationMultiselect) {
          this.$refs.locationMultiselect.search = this.locationSearchQuery;
        }
      });
    },
    onDatasheetSelect() {
      this.$nextTick(() => {
        if (this.$refs.datasheetMultiselect) {
          this.$refs.datasheetMultiselect.search = this.datasheetSearchQuery;
        }
      });
    },
    onMemberSelect() {
      this.$nextTick(() => {
        if (this.$refs.memberMultiselect) {
          this.$refs.memberMultiselect.search = this.memberSearchQuery;
        }
      });
    },
    filterDatasheets(searchText) {
      if (!this.allDatasheets) return;
      let lower = searchText ? searchText.toLowerCase() : '';
      this.datasheetOptions = this.allDatasheets
        .filter(ds => !lower || (ds.name && ds.name.toLowerCase().includes(lower)))
        .sort((a, b) => {
          // Sort by published and then by name
          if (a.published !== b.published) {
            if (a.published > b.published) {
              return -1
            } else {
              return 1
            }
          }
          let ad = a.name.toLowerCase(),
            bd = b.name.toLowerCase();
          if (ad < bd) return -1;
          if (ad > bd) return 1;
          return 0;
        })
        .slice(0, 5);
    },
    searchLocations(searchText) {
      this.locationLoading = true;
      this.fetchFilterLocations(searchText)
        .then(locations => {
          this.locationOptions = locations;
          this.locationLoading = false;
        })
        .catch(() => {
          this.locationLoading = false;
        });
    },
    searchMembers(searchText) {
      this.memberLoading = true;
      this.fetchFilterMembers(searchText)
        .then(members => {
          this.memberOptions = members;
          this.memberLoading = false;
        })
        .catch(() => {
          this.memberLoading = false;
        });
    },
    enterKeyHandler(e) {
      // Skip if key pressed is not 'Enter'
      if (e.key !== 'Enter') return;

      // Ensure the user is actually interacting with the filter panel
      //  - the click was in the background, the target is document.body
      //  - the click is in another component, we do not want to hijack the Enter key press
      const isInsideComponent = this.$el.contains(e.target);
      const isBody = e.target === document.body;
      if (!isInsideComponent && !isBody) return;

      // Skip if any of our multiselect components are open
      const isAnyOpen = [
        this.$refs.locationMultiselect,
        this.$refs.datasheetMultiselect,
        this.$refs.memberMultiselect
      ].some(ms => ms && ms.isOpen);

      // vue-multiselect uses the Enter key to select/deselect the highlighted value
      if (isAnyOpen) return;

      // Apply the filter when a valid Enter is pressed
      e.preventDefault();   // Prevent standard browser behavior
      e.stopPropagation();  // Stop vue-multiselect from getting the event and opening the menu
      this.applyFilters();
    },
    applyFilters() {
      this.$emit('filters-updated', {
        activeState: {...this.filters}
      });
    },
    resetFilters() {
      this.filters = {locationNames: [], datasheetNames: [], year: '', memberNames: [], hasPhotos: false};
      this.locationOptions = this.initialLocations ? [...this.initialLocations] : [];
      this.memberOptions = this.initialMembers ? [...this.initialMembers] : [];
      this.datasheetOptions = this.allDatasheets ? this.allDatasheets.slice(0, 5) : [];
      this.$emit('filters-updated', {
        activeState: {...this.filters}
      });
    },
  }
};
</script>

<style scoped lang="scss">
.datasheet-tag {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
